import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  User 
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { configureAuth, usersApi } from '@/lib/apiClient';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

// Nom du claim Firebase pour cette application
// Par défaut on supporte plusieurs variantes pour éviter les problèmes de casse/espaces
const APP_CLAIM = import.meta.env.VITE_FIREBASE_APP_CLAIM || 'Enrichment App';
const APP_CLAIM_FALLBACKS = ['enrichmentApp', 'EnrichmentApp', 'Enrichment App'];
const APPS_LIST_KEYS = ['allowedApps', 'apps'];

// Clé de stockage pour le client sélectionné (persistance via localStorage)
const LOCAL_STORAGE_CLIENT_KEY = 'enrichment_app_selected_client';

const evaluateClaims = (claims: Record<string, unknown>): boolean => {
  // Normaliser les clés à tester (claim principal + fallbacks)
  const claimKeysToCheck = [APP_CLAIM, ...APP_CLAIM_FALLBACKS];

  // Vérifier si l'utilisateur a le claim pour cette application
  // Le claim peut être un booléen ou une string
  const hasDirectClaim = claimKeysToCheck.some(
    (key) => claims[key] === true || claims[key] === 'true'
  );

  // Ou via une liste d'applications (allowedApps, apps, etc.)
  const hasAppsList = APPS_LIST_KEYS.some((listKey) => {
    const listValue = claims[listKey] as unknown;
    return Array.isArray(listValue) && claimKeysToCheck.some((key) => listValue.includes(key));
  });

  return hasDirectClaim || hasAppsList;
};

interface AuthContextType {
  isAuthenticated: boolean;
  hasAccess: boolean;
  user: User | null;
  selectedClient: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; errorType?: 'firebase' | 'claims' | 'api' }>;
  logout: () => Promise<void>;
  setClient: (client: string) => void;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Composant interne qui a accès au queryClient
const AuthProviderInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const ensureUserAuthorized = async (email: string | null): Promise<boolean> => {
    if (!email) return false;

    try {
      // Vérifier d'abord le cache React Query pour éviter les appels API dupliqués
      const cachedUser = queryClient.getQueryData(['current-user', email]);
      if (cachedUser && typeof cachedUser === 'object' && 'id' in cachedUser) {
        const hasClients = Array.isArray((cachedUser as any).accessible_client_ids) && 
                         (cachedUser as any).accessible_client_ids.length > 0;
        return hasClients;
      }

      // Si pas en cache, faire l'appel API
      const currentUser = await usersApi.getUserByEmailUsersByEmailEmailGet(email);
      if (!currentUser || typeof currentUser.id !== 'number') {
        return false;
      }

      // Mettre en cache pour les prochains appels (utilisé par ClientSelection)
      queryClient.setQueryData(['current-user', email], currentUser);

      const hasClients = Array.isArray(currentUser.accessible_client_ids) && currentUser.accessible_client_ids.length > 0;
      return hasClients;
    } catch (error) {
      console.error('Erreur lors de la vérification utilisateur API:', error);
      return false;
    }
  };

  const checkUserClaims = async (firebaseUser: User | null, forceRefresh = false) => {
    if (!firebaseUser) {
      setHasAccess(false);
      setIsLoading(false);
      return;
    }

    try {
      // Récupérer le token ID avec les claims (forçage refresh)
      const tokenResult = await firebaseUser.getIdTokenResult(forceRefresh);
      const claims = tokenResult.claims;

      const userHasAccess = evaluateClaims(claims);
      setHasAccess(userHasAccess);
      
      // TODO: L'authentification API utilise temporairement HTTP Basic avec credentials depuis variables d'environnement
      // Une fois l'API migrée vers JWT, utiliser le token Firebase ici
      // if (userHasAccess) {
      //   const idToken = await firebaseUser.getIdToken();
      //   configureAuth(firebaseUser.email || '', idToken);
      // }
      
      return userHasAccess;
    } catch (error) {
      console.error('Error checking user claims:', error);
      setHasAccess(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      await checkUserClaims(firebaseUser);
      
      // Si l'utilisateur se déconnecte, nettoyer le client sélectionné
      if (!firebaseUser) {
        setSelectedClient(null);
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.removeItem(LOCAL_STORAGE_CLIENT_KEY);
          } catch (error) {
            console.error('Erreur lors du nettoyage du client en localStorage:', error);
          }
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Réhydratation du client sélectionné depuis localStorage au démarrage
  // Seulement si l'utilisateur est connecté et a accès
  useEffect(() => {
    if (typeof window === 'undefined' || !user || !hasAccess || isLoading) {
      return;
    }

    try {
      const storedClient = window.localStorage.getItem(LOCAL_STORAGE_CLIENT_KEY);
      if (storedClient && !selectedClient) {
        // Réhydrater le client depuis localStorage
        setSelectedClient(storedClient);
      }
    } catch (error) {
      console.error('Erreur lors de la lecture du client stocké en localStorage:', error);
    }
  }, [user, hasAccess, isLoading, selectedClient]);

  const login = async (email: string, password: string): Promise<{ success: boolean; errorType?: 'firebase' | 'claims' | 'api' }> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Essayer d'abord sans forcer le refresh (plus rapide si le token est encore valide)
      let access = await checkUserClaims(userCredential.user, false);
      
      // Si pas d'accès, forcer le refresh (peut être lent mais nécessaire si le token est expiré)
      if (!access) {
        access = await checkUserClaims(userCredential.user, true);
        if (!access) {
          toast({
            variant: 'destructive',
            title: "Vous n'êtes pas autorisé",
            description: "Vos droits d'accès sont insuffisants.",
          });
          await logout();
          return { success: false, errorType: 'claims' };
        }
      }

      const authorizedInApi = await ensureUserAuthorized(userCredential.user.email);
      if (!authorizedInApi) {
        toast({
          variant: 'destructive',
          title: "Vous n'êtes pas autorisé",
          description: "Vos droits d'accès sont insuffisants.",
        });
        await logout();
        return { success: false, errorType: 'api' };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, errorType: 'firebase' };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
      // Nettoyer le client sélectionné en mémoire et en localStorage
      setSelectedClient(null);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(LOCAL_STORAGE_CLIENT_KEY);
        } catch (error) {
          console.error('Erreur lors du nettoyage du client en localStorage:', error);
        }
      }
      // TODO: Réinitialiser l'authentification API une fois migré vers JWT
      // configureAuth('', '');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const setClient = (client: string) => {
    const normalizedClient = client || null;
    setSelectedClient(normalizedClient);

    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (normalizedClient) {
        window.localStorage.setItem(LOCAL_STORAGE_CLIENT_KEY, normalizedClient);
      } else {
        window.localStorage.removeItem(LOCAL_STORAGE_CLIENT_KEY);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du client en localStorage:', error);
    }
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error: any) {
      // Log l'erreur seulement en développement
      if (import.meta.env.DEV) {
        console.error('Reset password error:', error);
      }
      return { success: false, error: error?.code || 'unknown-error' };
    }
  };

  // L'utilisateur est authentifié ET a accès à cette application
  const isAuthenticated = !!user && hasAccess;

  return (
    <AuthContext.Provider value={{ isAuthenticated, hasAccess, user, selectedClient, login, logout, setClient, resetPassword, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return <AuthProviderInner>{children}</AuthProviderInner>;
};

// Export test helpers
export const __test = { 
  evaluateClaims, 
  APP_CLAIM, 
  APP_CLAIM_FALLBACKS, 
  APPS_LIST_KEYS,
  LOCAL_STORAGE_CLIENT_KEY,
};